/**
 * External dependencies
 */
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/sheet';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

/**
 * Internal dependencies
 */
import { UpdateSpacePayload } from '@Types/api';
import { Space } from '@Types/venue';
import { Combobox } from '../Combobox';
import { useVenues } from '../../hooks/useVenues';
import service from '../../service';

const FormSchema = z.object({
  venueId: z.string(),
});

type FormValues = z.infer<typeof FormSchema>;

export interface EditSpaceVenueSheetProps extends React.PropsWithChildren {
  space: Space;
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

export default function EditSpaceVenueSheet({
  space,
  isOpen,
  setIsOpen,
}: EditSpaceVenueSheetProps) {
  const queryClient = useQueryClient();

  const { data: venues } = useVenues({
    enabled: isOpen,
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      venueId: space.venueId,
    },
  });

  // Keep form in sync if space changes while sheet is open
  useEffect(() => {
    if (isOpen) {
      form.reset({
        venueId: space.venueId,
      });
    }
  }, [space, isOpen, form]);

  const venueItems = (venues ?? [])
    .map((venue) => ({
      value: venue.id,
      label: venue.name,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );

  const onSubmit = async (values: FormValues) => {
    const { venueId } = values;

    const body: UpdateSpacePayload = {
      venueId,
    };

    // clear any previous root error
    form.clearErrors('root');

    try {
      await service.updateSpace(space.id, body);

      await queryClient.invalidateQueries({ queryKey: ['space', space.id] });
      await queryClient.invalidateQueries({ queryKey: ['spaces'] });

      setIsOpen(false);
    } catch (error) {
      console.error('Error updating space:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to update this space. Please check your changes and try again.',
      });
    }
  };

  const handleOnOpenChange = (newIsSheetOpen: boolean) => {
    setIsOpen(newIsSheetOpen);

    if (!newIsSheetOpen) {
      // reset back to the current space values when closing
      form.reset({
        venueId: space.venueId,
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <Sheet open={isOpen} onOpenChange={handleOnOpenChange}>
      <SheetContent
        onPointerDownOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="h-full">
            <div className="flex h-full flex-col">
              <SheetHeader>
                <SheetTitle>{`Edit ${space.name}'s venue`}</SheetTitle>
              </SheetHeader>

              <SheetBody className="grow overflow-auto">
                {/* Top-level error banner */}
                {rootError && (
                  <div className="mb-4">
                    <Alert variant="destructive">
                      <AlertTitle>Something went wrong</AlertTitle>
                      <AlertDescription>{rootError}</AlertDescription>
                    </Alert>
                  </div>
                )}

                <div className="space-y-6 py-3">
                  {/* Venue */}
                  <FormField
                    control={form.control}
                    name="venueId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Venue</FormLabel>
                        <Combobox
                          inForm
                          id="venueId"
                          items={venueItems}
                          value={field.value}
                          onChange={field.onChange}
                          placeholder="Select a venue"
                          disabled={!venues?.length}
                          loading={!venues}
                          inModal
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </SheetBody>

              <SheetFooter>
                <Button
                  type="submit"
                  variant="primary"
                  loading={form.formState.isSubmitting}
                >
                  Update
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => handleOnOpenChange(false)}
                >
                  Cancel
                </Button>
              </SheetFooter>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
